import { api } from '../utils/api';
import {
  addAnalyticsEvent,
  getPendingAnalytics,
  markAnalyticsSynced,
  clearSyncedAnalytics,
  getCleanShutdown,
  setCleanShutdown,
} from '../utils/storage';
import type { PlayEvent } from '../types';
import { PLAYER_CONFIG } from '../config';
import { manifestLoader } from './manifest-loader';
import { eventReporter } from './event-reporter';

type DeviceState = 'UNPAIRED' | 'PAIRING' | 'ONLINE' | 'OFFLINE' | 'ERROR' | 'MAINTENANCE';

class Analytics {
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private isFlushing = false;
  private currentState: DeviceState = 'ONLINE';
  private _isPlaying = false;
  private currentPlaylist: string | null = null;
  private currentAsset: string | null = null;
  private currentScheduleRuleId: string | undefined = undefined;
  private manifestVersion: number | undefined = undefined;
  private errorCode: string | undefined = undefined;
  private errorMessage: string | undefined = undefined;

  // App version reported in heartbeat. Platform shells (webOS/Tizen/Electron)
  // override the default via setAppVersion() during startup.
  private appVersion: string = '1.0.0';

  // Platform-specific info (set by WebOS/Tizen/Electron platform layers)
  private platformInfo: {
    connectionType?: string;
    ipAddress?: string | null;
    displayResolution?: string | null;
    model?: string | null;
    firmwareVersion?: string | null;
    wifiSsid?: string | null;
    rssi?: number | null;
    screenBrightness?: number | null;
    hdmiConnected?: boolean | null;
  } = {};

  // Crash detection
  private unexpectedReboot = false;

  // Heartbeat latency tracking
  private lastHeartbeatLatencyMs: number | undefined = undefined;

  // Watchdog tracking
  private watchdogTriggered = false;

  // One-shot guard for the report-only memory-pressure warning. Reset on
  // page reload — a fresh session deserves a fresh heads-up if the heap
  // climbs back into the danger zone. Threshold is 75% of jsHeapSizeLimit.
  // Crossing this threshold does NOT trigger any reload or remediation —
  // it is purely a telemetry signal for ops dashboards.
  private memoryPressureWarningFired = false;

  // Buffering tracking
  private bufferingCount = 0;
  private bufferingDurationMs = 0;
  private bufferingStartTime: number | null = null;
  private videoEventsBound = false;

  private beforeUnloadHandler: (() => void) | null = null;

  // Video event handlers - stored for cleanup
  private videoWaitingHandler: ((e: Event) => void) | null = null;
  private videoPlayingHandler: ((e: Event) => void) | null = null;

  async start(): Promise<void> {
    // Prevent duplicate intervals if start() is called multiple times
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Check for unexpected reboot (clean shutdown flag)
    const wasCleanShutdown = await getCleanShutdown();
    if (!wasCleanShutdown) {
      // Previous session did not shut down cleanly
      this.unexpectedReboot = true;
      console.warn('[Analytics] Detected unexpected reboot (no clean shutdown flag)');
    }
    // Mark as unclean immediately — will be set clean on graceful shutdown
    await setCleanShutdown(false);

    // Register beforeunload to mark clean shutdown (remove previous listener first)
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    this.beforeUnloadHandler = () => {
      // Use localStorage for synchronous write (IndexedDB is async and may not complete)
      localStorage.setItem('govista_clean_shutdown', 'true');
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    // Start periodic flush
    this.flushInterval = setInterval(
      () => this.flush(),
      PLAYER_CONFIG.ANALYTICS_FLUSH_INTERVAL_MS
    );

    // Start heartbeat. Wrap in requestIdleCallback so the heartbeat work
    // (which does an async navigator.storage.estimate() scan of IndexedDB
    // usage, plus a network POST) can't preempt video playback during
    // steady-state playback. Without this guard, every ~30s the heartbeat
    // tick can produce a visible main-thread stall that users perceive as
    // a mid-playback freeze. Falls back to direct invocation on platforms
    // without requestIdleCallback (webOS shell on older firmware).
    const scheduleHeartbeat = () => {
      const ric = (window as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
      if (typeof ric === 'function') {
        ric(() => { void this.sendHeartbeat(); }, { timeout: 5000 });
      } else {
        void this.sendHeartbeat();
      }
    };
    this.heartbeatInterval = setInterval(
      scheduleHeartbeat,
      PLAYER_CONFIG.HEARTBEAT_INTERVAL_MS
    );

    // Bind video buffering event listeners
    this.bindVideoBufferingEvents();

    // Initial heartbeat
    this.sendHeartbeat();
  }

  stop(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    // Remove beforeunload handler
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    // Remove video buffering event listeners
    if (this.videoWaitingHandler) {
      document.removeEventListener('waiting', this.videoWaitingHandler, true);
      this.videoWaitingHandler = null;
    }
    if (this.videoPlayingHandler) {
      document.removeEventListener('playing', this.videoPlayingHandler, true);
      this.videoPlayingHandler = null;
    }
    this.videoEventsBound = false;
    // Mark clean shutdown
    setCleanShutdown(true);
  }

  setState(state: DeviceState): void {
    this.currentState = state;
  }

  setPlaying(playing: boolean): void {
    this._isPlaying = playing;
  }

  setManifestVersion(version: number): void {
    this.manifestVersion = version;
  }

  setError(code: string | undefined, message: string | undefined): void {
    this.errorCode = code;
    this.errorMessage = message;
    if (code) {
      this.currentState = 'ERROR';
    }
  }

  setCurrentContent(playlistId: string | null, assetId: string | null, scheduleRuleId?: string): void {
    this.currentPlaylist = playlistId;
    this.currentAsset = assetId;
    this.currentScheduleRuleId = scheduleRuleId;
  }

  setPlatformInfo(info: {
    connectionType?: string;
    ipAddress?: string | null;
    displayResolution?: string | null;
    model?: string | null;
    firmwareVersion?: string | null;
    wifiSsid?: string | null;
    rssi?: number | null;
    screenBrightness?: number | null;
    hdmiConnected?: boolean | null;
  }): void {
    this.platformInfo = { ...this.platformInfo, ...info };
  }

  setWatchdogTriggered(triggered: boolean): void {
    this.watchdogTriggered = triggered;
  }

  setAppVersion(version: string): void {
    if (version) this.appVersion = version;
  }

  async recordPlayEvent(event: PlayEvent): Promise<void> {
    await addAnalyticsEvent('play', event);
  }

  async recordSession(sessionData: {
    startedAt: string;
    endedAt?: string;
    assetIds: string[];
  }): Promise<void> {
    await addAnalyticsEvent('session', sessionData);
  }

  private async sendHeartbeat(): Promise<void> {
    const MAX_RETRIES = 3;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const metrics = await this.collectMetrics();

        // Report-only memory guard — one-shot 75% warning per session.
        // Crossing the threshold emits a MEMORY_PRESSURE_WARNING event so
        // ops can see the device drifting. We do not auto-reload, do not
        // track multiple consecutive samples, and do not escalate at 90%.
        // The dashboard surfaces the live ratio; humans decide.
        if (
          !this.memoryPressureWarningFired &&
          metrics.jsHeapUsedMb != null &&
          metrics.jsHeapLimitMb != null &&
          metrics.jsHeapLimitMb > 0
        ) {
          const ratio = metrics.jsHeapUsedMb / metrics.jsHeapLimitMb;
          if (ratio > 0.75) {
            this.memoryPressureWarningFired = true;
            eventReporter.reportMemoryPressure({
              ratio,
              jsHeapUsedMb: metrics.jsHeapUsedMb,
              jsHeapLimitMb: metrics.jsHeapLimitMb,
            });
          }
        }

        // Build heartbeat data with base fields
        const heartbeatData: Parameters<typeof api.sendHeartbeat>[0] = {
          online: true,
          state: this.currentState,
          errorCode: this.errorCode,
          errorMessage: this.errorMessage,
          diskFreeMb: metrics.diskFreeMb,
          diskTotalMb: metrics.diskTotalMb,
          memoryFreeMb: metrics.memoryFreeMb,
          // Report-only memory guard fields. Server stores both; dashboard
          // shows utilization badge. NOT used to trigger any client-side
          // action — see plan for why we deliberately picked report-only
          // over auto-reload at this stage.
          jsHeapUsedMb: metrics.jsHeapUsedMb,
          jsHeapLimitMb: metrics.jsHeapLimitMb,
          manifestVersion: this.manifestVersion,
          appVersion: this.appVersion,
          // 3A — Bundle version: separate from shell `appVersion`. Built into
          // the JS bundle at compile time by vite.config.ts. Lets the
          // dashboard show "shell v1.6.0 / bundle v1.5.56-2026-05-17-855b5f76"
          // per device so ops can tell whether a PWA SW update has actually
          // landed or whether the device is still on an old cached bundle.
          // Set to undefined for hosts (e.g. older Electron shells) that
          // don't define the global, so the server treats it as "unknown".
          bundleVersion: typeof __BUNDLE_VERSION__ !== 'undefined' ? __BUNDLE_VERSION__ : undefined,
          currentAssetId: this.currentAsset || undefined,
          currentPlaylistId: this.currentPlaylist || undefined,
          currentScheduleRuleId: this.currentScheduleRuleId,
          isPlaying: this._isPlaying,
          unexpectedReboot: this.unexpectedReboot,
          watchdogTriggered: this.watchdogTriggered || undefined,
          deviceTime: new Date().toISOString(),
        };

        // Clear one-shot flags after including them
        if (this.unexpectedReboot) {
          this.unexpectedReboot = false;
        }
        if (this.watchdogTriggered) {
          this.watchdogTriggered = false;
        }

        // Enrich with Electron extended system info if available
        if (window.electronAPI?.getExtendedSystemInfo) {
          try {
            const sysInfo = await window.electronAPI.getExtendedSystemInfo();
            if (sysInfo) {
              // Override browser estimates with real OS values
              heartbeatData.memoryFreeMb = sysInfo.memoryFreeMb;
              heartbeatData.memoryTotalMb = sysInfo.memoryTotalMb;
              heartbeatData.diskFreeMb = sysInfo.diskFreeMb;
              heartbeatData.diskTotalMb = sysInfo.diskTotalMb;
              heartbeatData.cpuUsagePercent = sysInfo.cpuUsagePercent;
              heartbeatData.cpuTemperature = sysInfo.cpuTemperature;
              heartbeatData.connectionType = sysInfo.connectionType;
              heartbeatData.ipAddress = sysInfo.ipAddress;
              heartbeatData.displayResolution = sysInfo.displayResolution;
              heartbeatData.displayRefreshRate = sysInfo.displayRefreshRate;
              heartbeatData.displayOn = sysInfo.displayOn;
              heartbeatData.hdmiConnected = sysInfo.hdmiConnected;
              heartbeatData.uptimeSeconds = sysInfo.uptimeSeconds;
              heartbeatData.lastBootAt = sysInfo.lastBootAt;
              heartbeatData.batteryLevel = sysInfo.batteryLevel;
              heartbeatData.batteryCharging = sysInfo.batteryCharging;
              heartbeatData.powerSource = sysInfo.powerSource;
              heartbeatData.threadCount = sysInfo.threadCount;
              heartbeatData.wifiSsid = sysInfo.wifiSsid;
              heartbeatData.rssi = sysInfo.rssi;
              heartbeatData.screenBrightness = sysInfo.screenBrightness;
            }
          } catch (e) {
            console.warn('Failed to get extended system info:', e);
          }
        }

        // Enrich with browser-native APIs when Electron is NOT available
        if (!window.electronAPI?.getExtendedSystemInfo) {
          try {
            // Battery API (Chromium)
            if ('getBattery' in navigator) {
              const battery = await (navigator as any).getBattery();
              heartbeatData.batteryLevel = Math.round(battery.level * 100);
              heartbeatData.batteryCharging = battery.charging;
            }
            // Network Information API (Chromium)
            const conn = (navigator as any).connection;
            if (conn) {
              if (conn.downlink && !heartbeatData.downloadSpeedKbps) {
                heartbeatData.downloadSpeedKbps = Math.round(conn.downlink * 1000);
              }
              if (conn.effectiveType && !heartbeatData.connectionType) {
                heartbeatData.connectionType = conn.effectiveType;
              }
            }
            // Uptime via performance.now()
            heartbeatData.uptimeSeconds = Math.floor(performance.now() / 1000);
          } catch {
            // Browser APIs may not be available
          }
        }

        // Enrich with platform-specific info (WebOS/Tizen)
        if (this.platformInfo.connectionType) heartbeatData.connectionType = this.platformInfo.connectionType;
        if (this.platformInfo.ipAddress) heartbeatData.ipAddress = this.platformInfo.ipAddress;
        if (this.platformInfo.displayResolution) heartbeatData.displayResolution = this.platformInfo.displayResolution;
        if (this.platformInfo.wifiSsid) heartbeatData.wifiSsid = this.platformInfo.wifiSsid;
        if (this.platformInfo.rssi != null) heartbeatData.rssi = this.platformInfo.rssi;
        if (this.platformInfo.screenBrightness != null) heartbeatData.screenBrightness = this.platformInfo.screenBrightness;
        if (this.platformInfo.hdmiConnected != null) heartbeatData.hdmiConnected = this.platformInfo.hdmiConnected;

        // Add download progress from manifest-loader
        const dlProgress = manifestLoader.getDownloadProgress();
        heartbeatData.contentSyncPercent = dlProgress.contentSyncPercent;
        heartbeatData.pendingDownloads = dlProgress.pendingDownloads;
        heartbeatData.failedDownloads = dlProgress.failedDownloads;

        // Add video playback stats
        const videoStats = this.collectVideoPlaybackStats();
        if (videoStats) {
          heartbeatData.droppedFrames = videoStats.droppedFrames;
          heartbeatData.playbackResolution = videoStats.playbackResolution;
          heartbeatData.videoCodec = videoStats.videoCodec;
          heartbeatData.audioCodec = videoStats.audioCodec;
          heartbeatData.bufferingCount = videoStats.bufferingCount;
          heartbeatData.bufferingDurationMs = videoStats.bufferingDurationMs;
          heartbeatData.volumeLevel = videoStats.volumeLevel;
          heartbeatData.audioMuted = videoStats.audioMuted;
        }

        // Include latency from previous heartbeat
        if (this.lastHeartbeatLatencyMs !== undefined) {
          heartbeatData.latencyMs = this.lastHeartbeatLatencyMs;
        }

        const heartbeatStartAbsolute = Date.now(); // For clock sync (absolute time)
        const heartbeatStart = performance.now();
        const response = await api.sendHeartbeat(heartbeatData);
        this.lastHeartbeatLatencyMs = Math.round(performance.now() - heartbeatStart);

        // Update clock offset for video wall NTP sync
        if (response.serverTime) {
          const { syncManager } = await import('./sync-manager');
          syncManager.updateClockOffset(response.serverTime, heartbeatStartAbsolute);
        }

        // Check if there's a manifest update available
        if (response.hasManifestUpdate) {
          console.log('Manifest update available, version:', response.latestManifestVersion);
          // Trigger manifest refresh (player will handle this)
          window.dispatchEvent(new CustomEvent('manifest-update-available'));
        }

        // Check for remote commands
        if (response.command) {
          window.dispatchEvent(new CustomEvent('remote-command', { detail: response.command }));
        }

        console.log('Heartbeat sent successfully');
        return; // Success — exit retry loop
      } catch (error) {
        if (attempt < MAX_RETRIES) {
          const backoffMs = 1000 * Math.pow(2, attempt - 1); // 1s, 2s, 4s
          console.warn(`Heartbeat attempt ${attempt} failed, retrying in ${backoffMs}ms...`);
          await new Promise(r => setTimeout(r, backoffMs));
        } else {
          console.error('Failed to send heartbeat after retries:', error);
        }
      }
    }
  }

  private bindVideoBufferingEvents(): void {
    if (this.videoEventsBound) return;
    this.videoEventsBound = true;

    // Create handlers and store references for cleanup
    this.videoWaitingHandler = (e: Event) => {
      if (e.target instanceof HTMLVideoElement) {
        this.bufferingCount++;
        this.bufferingStartTime = Date.now();
      }
    };

    this.videoPlayingHandler = (e: Event) => {
      if (e.target instanceof HTMLVideoElement && this.bufferingStartTime) {
        this.bufferingDurationMs += Date.now() - this.bufferingStartTime;
        this.bufferingStartTime = null;
      }
    };

    // Use event delegation on document to catch video events from dynamically created elements
    document.addEventListener('waiting', this.videoWaitingHandler, true);
    document.addEventListener('playing', this.videoPlayingHandler, true);
  }

  private collectVideoPlaybackStats(): {
    droppedFrames: number;
    playbackResolution: string | null;
    videoCodec: string | null;
    audioCodec: string | null;
    bufferingCount: number;
    bufferingDurationMs: number;
    volumeLevel: number;
    audioMuted: boolean;
  } | null {
    try {
      const video = document.querySelector('video');
      if (!video) return null;

      const quality = video.getVideoPlaybackQuality?.();
      const droppedFrames = quality?.droppedVideoFrames ?? 0;

      const playbackResolution =
        video.videoWidth && video.videoHeight
          ? `${video.videoWidth}x${video.videoHeight}`
          : null;

      // Get codec info from video element if available via MediaSource
      let videoCodec: string | null = null;
      let audioCodec: string | null = null;
      try {
        const src = video.src || '';
        if (src.includes('codecs=')) {
          const codecMatch = src.match(/codecs="([^"]+)"/);
          if (codecMatch) {
            const parts = codecMatch[1].split(',').map((s: string) => s.trim());
            videoCodec = parts[0] || null;
            audioCodec = parts[1] || null;
          }
        }
      } catch {
        // Ignore codec detection errors
      }

      // Volume/audio state
      const volumeLevel = Math.round(video.volume * 100);
      const audioMuted = video.muted || video.volume === 0;

      // Collect and reset buffering stats
      const stats = {
        droppedFrames,
        playbackResolution,
        videoCodec,
        audioCodec,
        bufferingCount: this.bufferingCount,
        bufferingDurationMs: this.bufferingDurationMs,
        volumeLevel,
        audioMuted,
      };

      // Reset for next heartbeat period
      this.bufferingCount = 0;
      this.bufferingDurationMs = 0;

      return stats;
    } catch {
      return null;
    }
  }

  private async collectMetrics(): Promise<{
    diskFreeMb: number;
    diskTotalMb: number;
    memoryFreeMb: number;
    jsHeapUsedMb: number | null;
    jsHeapLimitMb: number | null;
  }> {
    let diskFreeMb = 1000; // Default 1GB
    let diskTotalMb = 1000; // Default 1GB
    let memoryFreeMb = 512; // Default 512MB
    // Default null — only Chromium-based browsers expose performance.memory.
    // Safari + Firefox return undefined; webOS / Electron / Chrome do.
    let jsHeapUsedMb: number | null = null;
    let jsHeapLimitMb: number | null = null;

    // Try to get storage info. navigator.storage.estimate() reports the
    // browser's allocated quota for THIS ORIGIN — which is the relevant
    // budget for the IndexedDB asset cache. We also report `diskTotalMb`
    // so the server-side "% free" calculation in admin/devices.ts uses
    // a sensible denominator. Without this the server defaults to 32GB,
    // making any browser-based player look "critically full" even when
    // it has plenty of room for the playlist.
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        if (estimate.quota !== undefined && estimate.usage !== undefined) {
          diskFreeMb = Math.floor((estimate.quota - estimate.usage) / (1024 * 1024));
          diskTotalMb = Math.floor(estimate.quota / (1024 * 1024));
        }
      } catch {
        // Ignore storage errors
      }
    }

    // Try to get memory info (Chrome only)
    if ('memory' in performance) {
      const memory = (performance as { memory?: { jsHeapSizeLimit?: number; usedJSHeapSize?: number } }).memory;
      if (memory?.jsHeapSizeLimit && memory?.usedJSHeapSize) {
        memoryFreeMb = Math.floor((memory.jsHeapSizeLimit - memory.usedJSHeapSize) / (1024 * 1024));
        // Also expose the raw used + limit so the dashboard can compute
        // utilization ratio for the "memory pressure" badge. Sent in the
        // heartbeat alongside legacy memoryFreeMb (which stays for backwards
        // compat — Android also populates that field via JVM).
        jsHeapUsedMb = Math.floor(memory.usedJSHeapSize / (1024 * 1024));
        jsHeapLimitMb = Math.floor(memory.jsHeapSizeLimit / (1024 * 1024));
      }
    }

    return { diskFreeMb, diskTotalMb, memoryFreeMb, jsHeapUsedMb, jsHeapLimitMb };
  }

  async flush(): Promise<void> {
    if (this.isFlushing) {
      return;
    }

    this.isFlushing = true;

    try {
      const pending = await getPendingAnalytics();

      if (pending.length === 0) {
        return;
      }

      console.log(`Flushing ${pending.length} analytics events...`);

      // Group play events for batch upload
      const playEvents = pending.filter((e) => e.type === 'play');

      if (playEvents.length > 0) {
        try {
          await api.batchReportPlays(
            playEvents.map((e) => e.data as {
              assetId: string;
              playlistId: string;
              startedAt: string;
              endedAt: string;
              durationMs: number;
              completed: boolean;
            })
          );
          await markAnalyticsSynced(playEvents.map((e) => e.id));
        } catch (error) {
          console.error('Failed to sync play events:', error);
        }
      }

      // Send session events individually
      const sessionEvents = pending.filter((e) => e.type === 'session');

      for (const event of sessionEvents) {
        try {
          await api.reportSession(event.data as {
            startedAt: string;
            endedAt?: string;
            assetIds: string[];
          });
          await markAnalyticsSynced([event.id]);
        } catch (error) {
          console.error('Failed to sync session event:', error);
        }
      }

      // Clean up synced events
      await clearSyncedAnalytics();

      console.log('Analytics flush complete');
    } catch (error) {
      console.error('Analytics flush error:', error);
    } finally {
      this.isFlushing = false;
    }
  }

  // Force flush before page unload
  async forceFlush(): Promise<void> {
    this.isFlushing = false; // Allow force flush even if already flushing
    await this.flush();
  }

  // Force-send a heartbeat immediately (used by platform shells on resume
  // from background, so the device doesn't show as OFFLINE while the next
  // periodic heartbeat is still pending).
  async forceHeartbeat(): Promise<void> {
    await this.sendHeartbeat();
  }
}

export const analytics = new Analytics();
